import { ReactNode, SyntheticEvent, useMemo, useState } from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCircleQuestion, faCircleXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CheckboxGroupOptions, CheckboxGroupVariants } from '@sage/ui-components';

import DrawerBody from './DrawerBody';
import DrawerFooter from './DrawerFooter';
import { DrawerComponentProps } from './DrawerManager';
import DrawerTitle from './DrawerTitle';
import { ObjectTypes, useAnalytics } from 'src/AnalyticsContext';
import { AlertOutcomeTypes } from 'src/constants/AlertOutcomeTypes';
import { Drawers } from 'src/constants/Drawers';
import { PlannedCareResolutionTypes, ResolutionTypes } from 'src/constants/ResolutionTypes';
import { CareOutcome, Task } from 'src/store/sageAdminApi';

const DrawerName = 'Select Proactive Resolution Drawer';

export interface SelectProactiveResolutionDrawerProps extends DrawerComponentProps {
    analyticsPrefix?: string;
    /** Carried forward to the observations and resolve drawers */
    task?: Task;
}

interface OptionLabelProps {
    icon: IconProp;
    label: string;
    description?: string;
    isEmergency?: boolean;
    isDisabled?: boolean;
}

// TODO(@robsli): extract into a shared ResolutionOptionLabel used by the other resolution drawers
function OptionLabel({
    icon,
    label,
    description,
    isEmergency = false,
    isDisabled = false,
}: OptionLabelProps) {
    const { spacing, palette } = useTheme();

    return (
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: spacing(5),
                    height: spacing(5),
                }}
            >
                <FontAwesomeIcon
                    icon={icon}
                    color={isEmergency ? palette.error.main : palette.common.black}
                    size="2x"
                    fixedWidth
                    style={{ opacity: isDisabled ? 0.3 : 1 }}
                />
            </Box>
            <Stack sx={{ flex: 1 }}>
                <Typography
                    variant="body1"
                    sx={{ fontWeight: 'bold', color: isDisabled ? 'grey.300' : 'common.black' }}
                >
                    {label}
                </Typography>
                {description && (
                    <Typography
                        variant="body2"
                        sx={{ color: isDisabled ? 'grey.300' : 'grey.700' }}
                    >
                        {description}
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
}

interface SectionProps {
    title: string;
    children: ReactNode;
}

function Section({ title, children }: SectionProps) {
    return (
        <Stack sx={{ p: 2.5, gap: 1.5 }}>
            <Typography variant="subtitle1" component="h2" sx={{ textTransform: 'uppercase' }}>
                {title}
            </Typography>
            {children}
        </Stack>
    );
}

function SelectProactiveResolutionDrawer(props: SelectProactiveResolutionDrawerProps) {
    const { onNext, hasNextDrawer } = props;

    const { trackEvent } = useAnalytics();

    const [selectedOptions, setSelectedOptions] = useState<Record<string, boolean>>({});

    const selectedCareProvided = useMemo(
        () => Object.keys(selectedOptions).filter((careProvided) => selectedOptions[careProvided]),
        [selectedOptions]
    );
    const isNoCareNeededSelected = !!selectedOptions[ResolutionTypes.NoCareGiven.id];
    const isCareProvidedSelected = selectedCareProvided.some(
        (careProvided) => careProvided !== ResolutionTypes.NoCareGiven.id
    );

    const handleSelectOption = (careProvided: string, isChecked: boolean) => {
        trackEvent('Resolution Type Clicked', {
            resolutionId: careProvided,
            isChecked,
            objectType: ObjectTypes.Checkbox,
            context: DrawerName,
        });
        setSelectedOptions((previous) => ({ ...previous, [careProvided]: isChecked }));
    };

    const handleSubmit = (e: SyntheticEvent) => {
        e.preventDefault();

        trackEvent('Resolution Options Selected', {
            context: DrawerName,
            numResolutionsSelected: selectedCareProvided.length,
            resolutionsSelected: selectedCareProvided,
        });

        const careOutcomes: CareOutcome[] = selectedCareProvided.map((careProvided) => ({
            outcome: 'COMPLETED',
            careProvided: careProvided as CareOutcome['careProvided'],
        }));

        onNext(
            {
                careOutcomes,
                alertOutcomeType: AlertOutcomeTypes.ResidentInteraction,
            },
            hasNextDrawer ? undefined : { type: Drawers.ResolveTask }
        );
    };

    return (
        <>
            <DrawerTitle sx={{ p: 0, gap: 2 }}>
                <Box sx={{ px: 2.5, pt: 9 }}>
                    <Typography variant="h2" sx={{ fontWeight: 'bold', textAlign: 'left' }}>
                        Select all care provided
                    </Typography>
                </Box>
                <Divider />
            </DrawerTitle>
            <DrawerBody sx={{ p: 0 }}>
                <Stack divider={<Divider />}>
                    <Section title="ADLs">
                        <CheckboxGroupOptions
                            variant={CheckboxGroupVariants.Outlined}
                            selectedOptions={selectedOptions}
                            onSelectOption={handleSelectOption}
                            formGroupProps={{ 'aria-label': 'ADL care provided' }}
                            options={[
                                {
                                    value: ResolutionTypes.Toileting.id,
                                    disabled: isNoCareNeededSelected,
                                    label: (
                                        <OptionLabel
                                            isDisabled={isNoCareNeededSelected}
                                            icon={ResolutionTypes.Toileting.icon!}
                                            label="Toileted"
                                        />
                                    ),
                                },
                                {
                                    value: ResolutionTypes.Transfer.id,
                                    disabled: isNoCareNeededSelected,
                                    label: (
                                        <OptionLabel
                                            isDisabled={isNoCareNeededSelected}
                                            icon={ResolutionTypes.Transfer.icon!}
                                            label="Returned to bed or chair"
                                        />
                                    ),
                                },
                            ]}
                        />
                    </Section>

                    <Section title="Emergency">
                        <CheckboxGroupOptions
                            variant={CheckboxGroupVariants.Outlined}
                            selectedOptions={selectedOptions}
                            onSelectOption={handleSelectOption}
                            formGroupProps={{ 'aria-label': 'Emergency care provided' }}
                            options={[
                                {
                                    value: ResolutionTypes.SeenOnFloor.id,
                                    disabled: isNoCareNeededSelected,
                                    label: (
                                        <OptionLabel
                                            isDisabled={isNoCareNeededSelected}
                                            icon={ResolutionTypes.Fall.icon!}
                                            label="Seen on floor"
                                            isEmergency
                                        />
                                    ),
                                },
                            ]}
                        />
                    </Section>

                    <Section title="Other">
                        <CheckboxGroupOptions
                            variant={CheckboxGroupVariants.Outlined}
                            selectedOptions={selectedOptions}
                            onSelectOption={handleSelectOption}
                            formGroupProps={{ 'aria-label': 'Other care provided' }}
                            options={[
                                {
                                    value: PlannedCareResolutionTypes.OtherSafety.id,
                                    disabled: isNoCareNeededSelected,
                                    label: (
                                        <OptionLabel
                                            isDisabled={isNoCareNeededSelected}
                                            icon={faCircleQuestion}
                                            label="Other care"
                                            description="Other care provided – note required"
                                        />
                                    ),
                                },
                                {
                                    value: ResolutionTypes.NoCareGiven.id,
                                    disabled: isCareProvidedSelected,
                                    label: (
                                        <OptionLabel
                                            isDisabled={isCareProvidedSelected}
                                            icon={faCircleXmark}
                                            label="No care needed"
                                            description="Resident settled, nothing required"
                                        />
                                    ),
                                },
                            ]}
                        />
                    </Section>
                </Stack>
            </DrawerBody>
            <DrawerFooter showShadow sx={{ bgcolor: 'common.white' }}>
                <Button
                    fullWidth
                    color="primary"
                    type="submit"
                    variant="contained"
                    disableElevation
                    disableFocusRipple
                    onClick={handleSubmit}
                    disabled={selectedCareProvided.length === 0}
                >
                    Submit
                </Button>
            </DrawerFooter>
        </>
    );
}

export default SelectProactiveResolutionDrawer;
